/**
 * Canonical task type IDs and labels (Timescale task_configs.task_type)
 */

/** @enum {number} Integer stored on task config documents as task_type / taskType */
const TaskType = Object.freeze({
  OTHER: 0,
  GROUND_PREP: 1,
  PLANTING: 2,
  SPRAY: 3,
  CROP_MAINTENANCE: 4,
  IRRIGATION: 5,
  HARVEST: 6,
});

const taskTypeMapping = {
  0: 'Other',
  1: 'Cultivating',
  2: 'Planting',
  3: 'Spray',
  4: 'Crop Maintenance',
  5: 'Irrigation',
  6: 'Harvest',
};

const taskTypeDescriptions = {
  0: 'Scouting, general',
  1: 'Preparing soil for crop i.e. plowing, discing, tillage, ripping, bed prep, cultivation, mulching',
  2: 'Establishing the crop i.e. transplanting, seeding',
  3: 'Applying material i.e. fungicide, herbicide, sulphur, dusting, 1-row/2-row',
  4: 'Managing crop and floor growth i.e. pruning, thinning, hedging, mowing',
  5: 'Delivering water/nutrients',
  6: 'Bringing in the crop i.e. picking, harvesting, hauling, shaking, sweeping',
};

/** Case-insensitive label → type int */
const taskTypeLabelToId = Object.freeze(
  Object.fromEntries(
    Object.entries(taskTypeMapping).map(([id, label]) => {
      return [String(label).toLowerCase(), Number(id)];
    })
  )
);

/**
 * Parse a task type value as int or human label (e.g. 'Crop Maintenance').
 * @param {*} value
 * @return {number|null} Known type int, or null if unrecognized
 */
function parseTaskType(value) {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' || (typeof value === 'string' && /^-?\d+$/.test(String(value).trim()))) {
    const parsed = parseInt(value, 10);
    if (Number.isFinite(parsed) && Object.prototype.hasOwnProperty.call(taskTypeMapping, parsed)) {
      return parsed;
    }
    return null;
  }
  const labelKey = String(value).trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(taskTypeLabelToId, labelKey)) {
    return taskTypeLabelToId[labelKey];
  }
  return null;
}

/**
 * Normalize a task_type value to a known integer; defaults to Other (0).
 * Accepts ints or human labels (e.g. 'Crop Maintenance').
 * @param {*} value
 * @return {number}
 */
function normalizeTaskType(value) {
  const parsed = parseTaskType(value);
  return parsed == null ? TaskType.OTHER : parsed;
}

/**
 * Resolve selected task entries (IDs and/or display names) to task config IDs.
 * Entries resolving to no config are kept verbatim ('' = no-task sentinel).
 * @param {Array<string>|null|undefined} selectedTasks
 * @param {Object} taskConfigsById
 * @return {Set<string>}
 */
function resolveSelectedTaskIds(selectedTasks, taskConfigsById) {
  const configs = taskConfigsById || {};
  const byName = new Map();
  Object.keys(configs).forEach((taskId) => {
    const name = configs[taskId] && configs[taskId].name;
    if (!name) {
      return;
    }
    const key = String(name).trim().toLowerCase();
    if (!byName.has(key)) {
      byName.set(key, []);
    }
    byName.get(key).push(taskId);
  });

  const result = new Set();
  (selectedTasks || []).forEach((entry) => {
    if (entry == null) {
      return;
    }
    const asStr = String(entry);
    if (Object.prototype.hasOwnProperty.call(configs, asStr)) {
      result.add(asStr);
      return;
    }
    const matches = byName.get(asStr.trim().toLowerCase());
    if (matches) {
      matches.forEach((id) => {
        result.add(id);
      });
      return;
    }
    // Entry resolves to no config: keep it verbatim so it can still raw-match row task ids
    // - '' is the no-task sentinel (rows with no task assigned)
    // - statsview summaries use the literal 'No Task Selected'
    result.add(asStr);
  });
  return result;
}

/**
 * Resolve the effective task ID list from individual task selections and/or task types.
 * Union semantics: effective = tasksMatchingTypes ∪ selectedTaskIds.
 * Task entries may be config IDs or human-readable task names.
 * Task types may be ints or human labels (e.g. 'Crop Maintenance').
 * @param {Array<string>|null|undefined} selectedTaskIds Individually selected task IDs or names
 * @param {Array<number|string>|null|undefined} selectedTaskTypes Selected task type ints or labels
 * @param {Object} taskConfigsById Map of taskId -> config (expects taskType on each)
 * @return {Array<string>|null} null = no task filter; otherwise list of task IDs to keep
 */
function resolveEffectiveTaskIds(selectedTaskIds, selectedTaskTypes, taskConfigsById) {
  const hasTasks = Array.isArray(selectedTaskIds) && selectedTaskIds.length > 0;
  const hasTypes = Array.isArray(selectedTaskTypes) && selectedTaskTypes.length > 0;
  if (!hasTasks && !hasTypes) {
    return null;
  }

  const result = new Set();
  if (hasTypes) {
    const typeSet = new Set();
    selectedTaskTypes.forEach((t) => {
      const parsed = parseTaskType(t);
      if (parsed != null) {
        typeSet.add(parsed);
      }
    });
    Object.keys(taskConfigsById || {}).forEach((taskId) => {
      const cfg = taskConfigsById[taskId];
      if (cfg && typeSet.has(normalizeTaskType(cfg.taskType))) {
        result.add(taskId);
      }
    });
  }
  if (hasTasks) {
    resolveSelectedTaskIds(selectedTaskIds, taskConfigsById).forEach((id) => {
      result.add(id);
    });
  }
  return Array.from(result);
}

/**
 * Resolve effective task display names from types ∪ selected names (Alerts / Industry).
 * Task types may be ints or human labels.
 * @param {Array<string>|null|undefined} selectedTaskNames
 * @param {Array<number|string>|null|undefined} selectedTaskTypes
 * @param {Object} taskConfigsById
 * @return {Array<string>|null} null = no filter
 */
function resolveEffectiveTaskNames(selectedTaskNames, selectedTaskTypes, taskConfigsById) {
  const hasNames = Array.isArray(selectedTaskNames) && selectedTaskNames.length > 0;
  const hasTypes = Array.isArray(selectedTaskTypes) && selectedTaskTypes.length > 0;
  if (!hasNames && !hasTypes) {
    return null;
  }

  const result = new Set();
  if (hasTypes) {
    const typeSet = new Set();
    selectedTaskTypes.forEach((t) => {
      const parsed = parseTaskType(t);
      if (parsed != null) {
        typeSet.add(parsed);
      }
    });
    Object.keys(taskConfigsById || {}).forEach((taskId) => {
      const cfg = taskConfigsById[taskId];
      if (cfg && cfg.name && typeSet.has(normalizeTaskType(cfg.taskType))) {
        result.add(cfg.name);
      }
    });
  }
  if (hasNames) {
    selectedTaskNames.forEach((name) => {
      result.add(name);
    });
  }
  return Array.from(result);
}

module.exports = {
  TaskType,
  taskTypeMapping,
  taskTypeDescriptions,
  parseTaskType,
  normalizeTaskType,
  resolveEffectiveTaskIds,
  resolveEffectiveTaskNames,
};
